import { Button, Input, Textarea } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  Loader2,
  Globe,
  Lock,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { articleApi } from '../lib/api';

export function ArticleEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [accessLevel, setAccessLevel] = useState('public');
  const [isPublished, setIsPublished] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isNew && id) {
      fetchArticle(id);
    }
  }, [id]);

  async function fetchArticle(articleId: string) {
    setLoading(true);

    const { data: doc } = await supabase
      .from('document_sources')
      .select('*')
      .eq('id', articleId)
      .single();

    if (doc) {
      setTitle(doc.title);
      setDescription(doc.description || '');
      setCategory(doc.category || '');
      setAccessLevel(doc.access_level);
      setIsPublished(doc.is_published || false);
      setDisplayOrder(doc.display_order || 0);

      // Fetch content from chunks
      const { data: chunks } = await supabase
        .from('document_chunks')
        .select('content, chunk_index')
        .eq('source_id', articleId)
        .order('chunk_index', { ascending: true });

      setContent((chunks || []).map((c) => c.content).join('\n\n'));
    }

    setLoading(false);
  }

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!content.trim()) {
      setError('Content is required');
      return;
    }
    if (!profile?.organization_id) return;

    setSaving(true);
    setError('');

    // Writes go through help-center-admin so the article is properly chunked
    // with overlap and each chunk is embedded. Without that, articles are
    // invisible to hybrid_search and RAG-grounded answers skip them.
    try {
      if (isNew) {
        const result = await articleApi.create({
          title: title.trim(),
          description: description.trim() || null,
          category: category.trim() || null,
          content: content.trim(),
          isPublished,
          accessLevel: accessLevel as 'public' | 'internal' | 'restricted',
          displayOrder,
        });
        navigate(`/documents/edit/${result.id}`);
      } else if (id) {
        await articleApi.update(id, {
          title: title.trim(),
          description: description.trim() || null,
          category: category.trim() || null,
          content: content.trim(),
          isPublished,
          accessLevel: accessLevel as 'public' | 'internal' | 'restricted',
          displayOrder,
        });
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-link" />
      </div>
    );
  }

  return (
    <div className="support-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button aria-label="Go back" size="iconSm" variant="ghost"
            onClick={() => navigate('/documents')}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="support-page-title">
            {isNew ? 'New Article' : 'Edit Article'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted rounded-lg transition-colors"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
          <Button variant="ghost"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary disabled:bg-primary transition-colors text-sm font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-error-text">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Editor / Preview */}
        <div className="lg:col-span-3">
          <div className="bg-card rounded-lg border border-border">
            {/* Title */}
            <div className="p-6 border-b border-border">
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article title"
                className="w-full text-2xl font-bold text-foreground placeholder-gray-300 border-0 focus:ring-0 p-0"
              />
              <Input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description (shown in article list)"
                className="w-full text-muted-foreground mt-2 border-0 focus:ring-0 p-0 text-sm"
              />
            </div>

            {/* Content */}
            <div className="p-6">
              {showPreview ? (
                <div className="prose prose-gray max-w-none min-h-[400px]">
                  {content.split('\n').map((line, i) => (
                    <p key={i} className="mb-4 text-foreground leading-relaxed">
                      {line || '\u00A0'}
                    </p>
                  ))}
                </div>
              ) : (
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your article content here..."
                  className="w-full min-h-[400px] border-0 focus:ring-0 p-0 text-foreground leading-relaxed resize-none"
                />
              )}
            </div>
          </div>
        </div>

        {/* Sidebar settings */}
        <div className="space-y-4">
          {/* Publish */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h3 className="font-semibold text-foreground mb-3">Publish</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="rounded border-border text-link focus:ring-ring"
              />
              <span className="text-sm text-foreground">Published</span>
            </label>
            <div className="flex items-center gap-2 mt-3">
              {isPublished ? (
                <Globe className="w-4 h-4 text-success-foreground" />
              ) : (
                <Lock className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-xs text-muted-foreground">
                {isPublished ? 'Visible to customers' : 'Draft — not visible'}
              </span>
            </div>
          </div>

          {/* Access Level */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h3 className="font-semibold text-foreground mb-3">Access</h3>
            <select
              value={accessLevel}
              onChange={(e) => setAccessLevel(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring"
            >
              <option value="public">Public</option>
              <option value="internal">Internal Only</option>
              <option value="restricted">Restricted</option>
            </select>
          </div>

          {/* Category */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h3 className="font-semibold text-foreground mb-3">Category</h3>
            <Input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Getting Started"
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Display Order */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h3 className="font-semibold text-foreground mb-3">Order</h3>
            <Input
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring"
            />
            <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first</p>
          </div>
        </div>
      </div>
    </div>
  );
}
